import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import PasswordStrength from '@/components/PasswordStrength';
import { pickWelcomeBackground } from '@/lib/branding';
import zxcvbn from 'zxcvbn';

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bg] = useState(pickWelcomeBackground);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    const s = zxcvbn(password).score;
    if (s < 2) { toast.error('Password too weak. Try a longer passphrase or add symbols.'); return; }
    setLoading(true);
    try {
      await http.post('/auth/reset-password', { token, password });
      toast.success('Password reset. Please sign in with your new password.');
      nav('/login');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Reset failed. This link may be expired.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy bg-cover bg-center relative flex items-center justify-center p-6" style={{ backgroundImage: `url(${bg})` }}>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/45 to-navy/85" />
      <div className="relative z-10 w-full max-w-md bg-white rounded-3xl p-8 card-soft" data-testid="reset-page">
        <Link to="/login" className="text-sm text-navy/60">← Back</Link>
        <h1 className="font-heading text-3xl mt-4 mb-2">Choose a new password</h1>
        <p className="text-navy/60 text-sm mb-6">
          Your reset link expires in 2 hours and only works once. Passphrases are stronger than tricky passwords.
        </p>
        {!token ? <p className="text-red-600 text-sm">Missing or invalid reset token.</p> : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <input
                data-testid="reset-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={e=>setPassword(e.target.value)}
                placeholder="New password"
                className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <PasswordStrength password={password} data-testid="reset-password-strength" />
            </div>
            <button
              data-testid="reset-submit" disabled={loading}
              className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft disabled:opacity-50"
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
